<!-- src/components/ProjectGridComponent.vue -->
<template>
  <section class="py-6 px-4 mx-auto max-w-screen-xl">
    <!-- Network Toggle - shown only on homepage -->
    <!-- Network Toggle (Corrected) -->
    <div
      v-if="isHomePage"
      class="flex mb-6 gap-2 inline-block p-2 rounded-full"
    >
      <button
        type="button"
        class="px-4 py-1.5 text-sm font-medium transition-all rounded-full"
        :class="{
          'bg-blue-600 text-white': currentNetwork === 'testnet',
          'text-blue-700 hover:bg-blue-200': currentNetwork !== 'testnet'
        }"
        @click.prevent="switchNetwork('testnet')"
      >
        Testnet <span class="ml-1 bg-blue-500 text-white px-1.5 py-0.5 rounded-md text-xs">{{ getProjectCountByNetwork('testnet') }}</span>
      </button>
      <button
        type="button"
        class="px-4 py-1.5 text-sm font-medium transition-all rounded-full"
        :class="{
          'bg-blue-600 text-white': currentNetwork === 'mainnet',
          'text-blue-700 hover:bg-blue-200': currentNetwork !== 'mainnet'
        }"
        @click.prevent="switchNetwork('mainnet')"
      >
        Mainnet <span class="ml-1 bg-blue-500 text-white px-1.5 py-0.5 rounded-md text-xs">{{ getProjectCountByNetwork('mainnet') }}</span>
      </button>
    </div>
    
    <!-- Network Title - shown on network pages -->
    <div
      v-if="isNetworkPage"
      class="mb-6"
    >
      <h2 class="text-2xl font-bold text-gray-800 capitalize">
        {{ currentNetwork }} Projects
      </h2>
      <a
        href="/"
        class="text-blue-600 hover:text-blue-800 text-sm mt-1 inline-block"
      >← Back to Home</a>
    </div>

    <!-- Project Detail - shown on project detail pages -->
    <div
      v-if="isProjectPage"
      class="mb-6"
    >
      <div class="flex items-center gap-2">
        <a
          :href="`/${currentNetwork}`"
          class="text-blue-600 hover:text-blue-800 text-sm"
        >← Back to {{ currentNetwork }}</a>
      </div>
    </div>
  
    <!-- Project Grid - shown on home and network pages -->
    <div
      v-if="!isProjectPage"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8"
    >
      <div
        v-for="(project, index) in filteredProjects"
        :key="index"
        class="group relative"
      >
        <a
          :href="`/${project.network}/${project.name.toLowerCase()}`"
          class="block"
        >
          <!-- Card -->
          <div class="bg-blue-100 rounded-xl overflow-hidden transition-all hover:bg-blue-200 p-8 flex flex-col items-center justify-center h-full">
            <!-- Logo (now circular) -->
            <div class="flex items-center justify-center h-32 w-32 mb-4">
              <img
                :src="project.logo"
                class="h-28 w-28 object-cover rounded-full"
                alt="Project Logo"
              >
            </div>

            <!-- Project Name and Stake Button -->
            <div class="flex flex-col items-center w-full">
              <h3 class="text-base font-medium text-gray-800 text-center truncate w-full mb-2">
                {{ project.name }}
              </h3>
              <a
                v-if="project.network === 'testnet' && ['Og', 'Kiichain', 'Zenrock', 'Galatica'].includes(project.name)"
                :href="project.name === 'Og' ? 'https://explorer.winnode.xyz/0g-test/staking/0gvaloper1fnem6r4c3nxqcp5tcrpxgg4k2f5enj2scu90tr' : project.name === 'Kiichain' ? 'https://explorer.winnode.xyz/kiichain-test/staking/kiivaloper1zlnexclwk2r0fpur45akfyyjwmsf2m34p8l038' : project.name === 'Galatica' ? 'https://explorer.winnode.xyz/galactica-test/staking/galavaloper104v3j5wxsppqnftxjs2k37h6qs684ywgf0unq3' : `https://${project.name.toLowerCase()}.winnode.my.id`"
                target="_blank"
                class="px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm hover:bg-blue-700 transition-all"
              >
                Stake Now
              </a>
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- Project Detail Content - shown on project pages -->
    <div
      v-if="isProjectPage && currentProject"
      class="mt-6"
    >
      <!-- Project Header Card -->
      <div class="bg-white rounded-xl p-6 shadow-sm mb-6">
        <div class="flex items-center mb-4">
          <img
            :src="currentProject.logo"
            class="h-16 w-16 object-cover rounded-full mr-4"
            alt="Project Logo"
          >
          <div>
            <h2 class="text-xl font-bold">
              {{ currentProject.name }}
            </h2>
            <p class="text-gray-600">
              {{ currentProject.category }} • {{ currentProject.network }}
            </p>
          </div>
        </div>
        
        <!-- Blockchain Stats - shown at the top if available -->
        <BlockchainStats 
          v-if="projectContent && projectContent.blockchainStats"
          :api-endpoint="projectContent.blockchainStats.apiEndpoint"
          class="mb-4"
        />
        
        <!-- Description -->
        <div
          v-if="projectContent"
          class="border-t pt-4 mt-2"
        >
          <p class="text-gray-800">
            {{ projectContent.description }}
          </p>
          
          <!-- Project links with icons -->
          <div
            v-if="projectContent.links"
            class="flex flex-wrap gap-3 mt-4"
          >
            <a 
              v-for="(link, name) in projectContent.links" 
              :key="name" 
              :href="link.url" 
              target="_blank"
              class="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-sm flex items-center"
            >
              <i
                :class="link.icon"
                class="mr-1"
              /> {{ name }}
            </a>
          </div>
        </div>
      </div>
      
      <!-- Project Content Sections -->
      <template v-if="projectContent && projectContent.sections">
        <div 
          v-for="(section, index) in projectContent.sections" 
          :key="index"
          class="bg-white rounded-xl p-6 shadow-sm mb-6"
        >
          <h3 class="text-lg font-bold mb-4">
            {{ section.title }}
          </h3>
          
          <!-- Special treatment for blockchain-stats section type -->
          <BlockchainStats 
            v-if="section.type === 'blockchain-stats'"
            :api-endpoint="section.apiEndpoint"
          />
          
          <!-- Special treatment for code-section type -->
          <CodeSection
            v-else-if="section.type === 'code-section'"
            :content="section.content"
          />
          
          <!-- Special treatment for markdown-tabs type -->
          <MarkdownTabs
            v-else-if="section.type === 'markdown-tabs'"
            :content="section.content"
          />
          
          <!-- Regular content section -->
          <div
            v-else
            v-html="section.content"
          />
        </div>
      </template>
      
      <!-- Loading state or error message -->
      <div
        v-if="isLoadingContent"
        class="bg-white rounded-xl p-6 shadow-sm mb-6 text-center"
      >
        <p>Loading project information...</p>
      </div>
      
      <div
        v-if="contentError"
        class="bg-white rounded-xl p-6 shadow-sm mb-6 text-center text-red-500"
      >
        <p>{{ contentError }}</p>
      </div>
    </div>
  
    <!-- Pagination Controls (if needed) - shown on home and network pages -->
    <div
      v-if="!isProjectPage && displayedCount < totalFilteredCount"
      class="flex justify-center mt-6"
    >
      <button
        class="px-6 py-2 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 transition"
        @click="loadMore"
      >
        Show More Projects
      </button>
    </div>
  </section>
</template>

<script>
// Import Components
import BlockchainStats from './BlockchainStats.vue';
import CodeSection from './CodeSection.vue';
import MarkdownTabs from './MarkdownTabs.vue';

export default {
  name: 'ProjectGridComponent',
  components: {
    BlockchainStats,
    CodeSection,
    MarkdownTabs
  },
  data() {
    return {
      currentNetwork: "testnet",
      displayedCount: 20,
      projects: [
        { 
          name: "Og", 
          category: "Tech/Community", 
          status: "active", 
          network: "testnet",
          logo: "https://pbs.twimg.com/profile_images/1841632086475866113/n6vPDZqb_400x400.jpg"
        },
        { 
          name: "Kiichain", 
          category: "Infrastructure", 
          status: "active", 
          network: "testnet",
          logo: "https://pbs.twimg.com/profile_images/1800553180083666944/zZe128CW_400x400.jpg"
        },
        { 
          name: "Zenrock", 
          category: "Tech", 
          status: "active", 
          network: "testnet",
          logo: "https://pbs.twimg.com/profile_images/1829585852831285259/EAxFe-gB_400x400.png"
        },
        { 
          name: "Galatica", 
          category: "Infrastructure", 
          status: "active", 
          network: "testnet",
          logo: "https://pbs.twimg.com/profile_images/1853803085899923456/euxWKp-y_400x400.jpg"
        },

        // Mainet Susun di bawahnya 
        { 
          name: "Crossfi", 
          category: "Blockchain", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1719733805483790336/9rtdiMS-_400x400.jpg"
        },
        { 
          name: "Namada", 
          category: "Privacy", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1775529113102561281/y4D30_VO_400x400.jpg"
        },
        { 
          name: "Pactus", 
          category: "Blockchain", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1767588889743785984/lvzTWqcm_400x400.jpg"
        },
        { 
          name: "Story", 
          category: "Infrastructure", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1820303986349805569/MKfPfLtz_400x400.jpg"
        },
        { 
          name: "Entangle", 
          category: "Infrastructure", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1863589515962781696/cbVG9Om5_400x400.jpg"
        },
        { 
          name: "Matra-Chain", 
          category: "Infrastructure", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1898927282028150784/6eE_i7Xg_400x400.jpg"
        },
        { 
          name: "Taiko", 
          category: "Infrastructure", 
          status: "active", 
          network: "mainnet",
          logo: "https://pbs.twimg.com/profile_images/1823377013186691072/jPGLrNT0_400x400.png"
        },
      ],
      currentPath: '',
      currentProjectName: '',
      projectContent: null,
      isLoadingContent: false,
      contentError: null
    };
  },
  computed: {
    // Determine the current page type based on route
    isHomePage() {
      return this.currentPath === '/' || this.currentPath === '';
    },
    isNetworkPage() {
      return this.currentPath === '/testnet' || this.currentPath === '/mainnet';
    },
    isProjectPage() {
      return this.currentProjectName !== '';
    },
    // Filter projects based on current network and displayed count
    filteredProjects() {
      return this.projects
        .filter(project => {
          // On network pages or home, filter by the current network
          if (this.isHomePage || this.isNetworkPage) {
            return project.network === this.currentNetwork;
          }
          return true; // No filtering on project pages
        })
        .slice(0, this.displayedCount);
    },
    // Get total count of filtered projects
    totalFilteredCount() {
      return this.projects.filter(project => project.network === this.currentNetwork).length;
    },
    // Get the current project if on a project page
    currentProject() {
      if (!this.currentProjectName) return null;
      return this.projects.find(
        p => p.name.toLowerCase() === this.currentProjectName && p.network === this.currentNetwork
      );
    }
  },
  
  watch: {
    // Watch currentProject to load content when it changes
    currentProject: {
      immediate: true,
      handler(newProject) {
        if (newProject && this.isProjectPage) {
          this.loadProjectContent();
        } else {
          this.projectContent = null;
        }
      }
    },
    
    // Watch for network changes to update content
    currentNetwork: {
      handler() {
        if (this.isHomePage || this.isNetworkPage) {
          // Reset displayed count when switching networks
          this.displayedCount = 20;
        }
      }
    }
  },
  mounted() {
    // Parse URL when component is mounted
    this.parseUrl();
    
    // Load project content if on a project page
    if (this.isProjectPage && this.currentProject) {
      this.loadProjectContent();
    }
    
    // Handle popstate events (browser back/forward)
    const handlePopState = (event) => {
      if (event.state && event.state.network) {
        this.currentNetwork = event.state.network;
      }
      this.parseUrl();
    };
    
    // Listen for route changes (if using history mode)
    window.addEventListener('popstate', handlePopState);
    
    // Add Font Awesome if not already loaded
    if (!document.getElementById('font-awesome-css')) {
      const fontAwesomeLink = document.createElement('link');
      fontAwesomeLink.id = 'font-awesome-css';
      fontAwesomeLink.rel = 'stylesheet';
      fontAwesomeLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(fontAwesomeLink);
    }
  },
  
  // Clean up event listener
  beforeUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
  },
  methods: {
    loadMore() {
      this.displayedCount += 10;
    },
    getProjectCountByNetwork(network) {
      return this.projects.filter(project => project.network === network).length;
    },
    // New method to handle network switching without page navigation
    switchNetwork(network) {
      this.currentNetwork = network;
      
      // Update URL without reloading the page if history API is available
      if (this.isHomePage && window.history && window.history.pushState) {
        const url = network === 'testnet' ? '/' : `/${network}`;
        window.history.pushState({ network: network }, '', url);
      }
    },
    // Parse URL to determine context
    parseUrl() {
      const path = window.location.pathname;
      this.currentPath = path;
      
      // Reset content state when URL changes
      this.projectContent = null;
      this.contentError = null;
      
      // Split path to get segments
      const segments = path.split('/').filter(Boolean);
      
      if (segments.length >= 1) {
        // First segment should be network (testnet or mainnet)
        const network = segments[0];
        if (network === 'testnet' || network === 'mainnet') {
          this.currentNetwork = network;
        }
        
        // Second segment is project name if exists
        if (segments.length >= 2) {
          this.currentProjectName = segments[1].toLowerCase();
        } else {
          this.currentProjectName = '';
        }
      } else {
        // Default to homepage
        this.currentNetwork = 'testnet';
        this.currentProjectName = '';
      }
    },
    
    // Load project content from individual JS files
    async loadProjectContent() {
      if (this.currentProject) {
        this.isLoadingContent = true;
        this.contentError = null;
        
        try {
          const projectName = this.currentProject.name.toLowerCase();
          const networkName = this.currentProject.network.toLowerCase();
          
          // Dynamic import of the project-specific JS file
          try {
            const projectModule = await import(`./project/${networkName}/${projectName}.js`);
            this.projectContent = projectModule.default;
            console.log('Content loaded successfully:', this.projectContent);
          } catch (importError) {
            console.error(`Could not import project content for ${projectName}:`, importError);
            this.contentError = `Konten untuk ${this.currentProject.name} tidak tersedia.`;
            this.projectContent = null;
          }
        } catch (error) {
          console.error('Error loading project content:', error);
          this.contentError = `Terjadi kesalahan saat memuat konten: ${error.message}`;
          this.projectContent = null;
        } finally {
          this.isLoadingContent = false;
        }
      } else {
        this.projectContent = null;
      }
    }
  }
};
</script>

<style scoped>
/* Container styling to ensure no unwanted decorative elements */
section {
  position: relative;
  overflow: hidden;
}

/* Ensure the grid takes appropriate space */
.grid {
  position: relative;
  z-index: 1;
}

/* Additional styles to ensure proper alignment and no decorative elements */
.bg-blue-100 {
  position: relative;
  z-index: 1;
}

/* Smooth transition for network switching */
.grid {
  transition: all 0.3s ease-in-out;
}

/* Ensure circular images display properly */
img.rounded-full {
  object-fit: cover;
  aspect-ratio: 1/1;
}
</style>
